/**
 * Prospect detail route: tabs for overview, chats, pipelines, teams/streams, and emails.
 *
 * Where: `/leads/:leadId` (see `App` routes).
 * Uses: `leadApi.getLeadDetail`, `LeadHeader`, `LeadOverview`, list components, `EmailsList`.
 * Behavior: query key includes user key for impersonation refresh; resync mutation; back navigates to leads list.
 */
import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  IconButton,
  Button,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { leadApi } from '@/api/lead';
import { useAuth } from '@/hooks/useAuth';
import {
  LeadHeader,
  LeadOverview,
  ChatsList,
  PipelinesList,
  EmailsList,
} from '@/components';
import { TeamsStreamsList } from '@/components/leads';

function TabPanel({ children, value, index }: { children?: React.ReactNode; value: number; index: number }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export const LeadDetails: React.FC = () => {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);

  // Include user key in query key to ensure proper data refresh on impersonation
  const { data, isLoading, error } = useQuery({
    queryKey: ['lead', user?.key, leadId],
    queryFn: () => leadApi.getLeadDetail(leadId!, { include: ['chats', 'pipelines', 'projects'], chats_limit: 5 }),
    enabled: !!leadId,
  });

  const lead = data?.output;
  const prospect = lead?.prospect || {};

  const kpis = useMemo(() => {
    const unseenChats = (lead?.chats?.items || []).filter((c: any) => c.seen_last_message === false).length;
    const pipelinesCount = (lead?.pipelines || []).length;
    const projectsCount = (lead?.project_prospects || []).length;
    return { unseenChats, pipelinesCount, projectsCount };
  }, [lead]);

  const isAdmin = user?.is_staff || user?.is_superuser;

  const resyncMutation = useMutation({
    mutationFn: () => leadApi.resyncLead(leadId!),
    onSuccess: () => {
      // Refresh lead details after successful resync
      queryClient.invalidateQueries({ queryKey: ['lead', user?.key, leadId] });
    },
  });

  const openInboxAll = () => navigate(`/inbox?prospect=${leadId}`);
  const openChat = (chatUuid: string) => navigate(`/inbox?chat_uuid=${chatUuid}`);
  const openStream = (streamUuid: string) => navigate(`/campaigns/${streamUuid}`);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !lead) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load lead. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <LeadHeader prospect={prospect} onOpenInbox={openInboxAll} />
          </Box>
          {isAdmin && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => resyncMutation.mutate()}
              disabled={resyncMutation.isPending || !leadId}
            >
              {resyncMutation.isPending ? 'Syncing…' : 'Resync from LinkedIn'}
            </Button>
          )}
        </Box>
        {resyncMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to resync lead from LinkedIn. Please try again.
          </Alert>
        )}
        {resyncMutation.isSuccess && !resyncMutation.isPending && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Lead profile synced from LinkedIn.
          </Alert>
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3 }}>
          <Tab label="Overview" />
          <Tab label={`Chats${kpis.unseenChats ? ` (${kpis.unseenChats} new)` : ''}`} />
          <Tab label={`Pipelines (${kpis.pipelinesCount})`} />
          <Tab label={`Experiments (${kpis.projectsCount})`} />
          <Tab label="Emails" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {/* Overview */}
        <TabPanel value={tab} index={0}>
          <LeadOverview
            prospect={prospect}
            prospectCompanies={lead.prospect_companies || []}
            chats={lead.chats?.items || []}
            pipelines={lead.pipelines || []}
            projectProspects={lead.project_prospects || []}
            kpis={kpis}
            onOpenChat={openChat}
            onOpenStream={openStream}
          />
        </TabPanel>

        {/* Chats */}
        <TabPanel value={tab} index={1}>
          <ChatsList
            chats={lead.chats?.items || []}
            onOpenChat={openChat}
            onOpenStream={openStream}
          />
        </TabPanel>

        {/* Pipelines */}
        <TabPanel value={tab} index={2}>
          <PipelinesList
            pipelines={lead.pipelines || []}
            onOpenStream={openStream}
          />
        </TabPanel>

        {/* Teams/Streams */}
        <TabPanel value={tab} index={3}>
          <TeamsStreamsList
            teamProspects={lead.project_prospects || []}
            onOpenStream={openStream}
          />
        </TabPanel>

        {/* Emails */}
        <TabPanel value={tab} index={4}>
          {leadId && (
            <EmailsList
              prospectId={leadId}
              showStreamColumn
              showOwnerColumn
            />
          )}
        </TabPanel>
      </Box>
    </Box>
  );
};

export default LeadDetails;
